/**
 * Historial de versiones del proyecto (doc. 02 §7, doc. 03 §3.5): huella del contenido, compresión
 * de las instantáneas y las dos reglas que deciden qué se guarda y qué se poda. Sin interfaz ni
 * almacenamiento: el repositorio las aplica.
 */

import { hashTexto } from '../nucleo/aleatoriedad/generador';

/**
 * Por qué existe una versión:
 * - `automatica`: al abrir el proyecto o cada 10 minutos de edición (`detalle` dice cuál);
 * - `manual`: «Guardar versión», con nombre opcional;
 * - `antes-de:<operación>`: antes de una operación de riesgo (eliminar un espacio, copiar los
 *   parámetros de otro espacio, restaurar una versión);
 * - `migracion`: el documento original, tal como estaba, antes de convertirlo a un esquema nuevo.
 */
export type MotivoVersion = 'automatica' | 'manual' | 'migracion' | `antes-de:${string}`;

export interface ResumenDeVersion {
  nombre: string;
  espacios: number;
  elementos: number;
}

export interface VersionGuardada {
  id: string;
  proyectoId: string;
  /** Correlativo dentro del proyecto: 1, 2, 3… (no se reutiliza al podar). */
  numero: number;
  creadoEn: string;
  motivo: MotivoVersion;
  /** `al-abrir`, `cada-10-min`, el nombre del espacio eliminado, el número restaurado… */
  detalle?: string;
  /** Nombre que le dio el usuario (versiones manuales). */
  nombre?: string;
  /** Esquema del documento guardado (una versión «migración» conserva el esquema anterior). */
  versionEsquema: number;
  versionApp: string;
  huella: string;
  /** Bytes que ocupa la instantánea en el almacenamiento (comprimida, si el navegador puede). */
  tamano: number;
  resumen: ResumenDeVersion;
}

/** Cada cuántos minutos de edición se guarda una versión automática. */
export const MINUTOS_ENTRE_AUTOMATICAS = 10;
/** Versiones automáticas que se conservan por proyecto; las manuales y de migración no se podan. */
export const AUTOMATICAS_CONSERVADAS = 50;

/** Campos que no son contenido: la fecha de guardado y dónde está organizado el proyecto. */
const FUERA_DE_LA_HUELLA = new Set(['actualizadoEn', 'espacioDeTrabajoId', 'carpetaId']);

/** JSON con las claves ordenadas: el mismo contenido da el mismo texto, venga de donde venga. */
export function jsonCanonico(valor: unknown): string {
  return JSON.stringify(ordenar(valor));
}

function ordenar(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(ordenar);
  if (v === null || typeof v !== 'object') return v;
  const o = v as Record<string, unknown>;
  const salida: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) if (o[k] !== undefined) salida[k] = ordenar(o[k]);
  return salida;
}

const hex = (n: number) => n.toString(16).padStart(8, '0');

/**
 * Huella del contenido del proyecto (64 bits en hexadecimal). No cambia al guardar sin editar, al
 * mover el proyecto de carpeta ni con el orden de las claves.
 */
export function huellaDeContenido(documento: unknown): string {
  const contenido: Record<string, unknown> = {};
  if (documento && typeof documento === 'object')
    for (const [k, v] of Object.entries(documento as Record<string, unknown>))
      if (!FUERA_DE_LA_HUELLA.has(k)) contenido[k] = v;
  const texto = jsonCanonico(contenido);
  return hex(hashTexto(texto, 0x9e3779b9)) + hex(hashTexto(texto, 0x85ebca6b));
}

/** Resumen para la lista del historial; tolera documentos de esquemas anteriores. */
export function resumenDeDocumento(documento: unknown): ResumenDeVersion {
  const d = (documento ?? {}) as { nombre?: unknown; espacios?: unknown };
  const espacios = Array.isArray(d.espacios) ? (d.espacios as { layout?: unknown }[]) : [];
  const elementos = espacios.reduce((a, e) => {
    const l = (e.layout ?? {}) as { elementos?: unknown };
    return a + (Array.isArray(l.elementos) ? l.elementos.length : 0);
  }, 0);
  return {
    nombre: typeof d.nombre === 'string' ? d.nombre : '',
    espacios: espacios.length,
    elementos,
  };
}

export const esAutomatica = (m: MotivoVersion): boolean =>
  m === 'automatica' || m.startsWith('antes-de:');

/**
 * Regla de retención: se conservan las últimas `AUTOMATICAS_CONSERVADAS` automáticas (incluidas
 * las «antes de»), todas las manuales y todas las de migración. Devuelve los id que sobran.
 */
export function versionesQueSobran(
  versiones: readonly Pick<VersionGuardada, 'id' | 'numero' | 'motivo'>[],
  conservar = AUTOMATICAS_CONSERVADAS,
): string[] {
  return versiones
    .filter((v) => esAutomatica(v.motivo))
    .sort((a, b) => b.numero - a.numero)
    .slice(conservar)
    .map((v) => v.id);
}

/**
 * ¿Toca una versión automática por tiempo de edición? Sí, si el contenido cambió desde la última
 * versión y pasaron al menos `MINUTOS_ENTRE_AUTOMATICAS` desde ella (o no hay ninguna).
 */
export function tocaVersionPorTiempo(
  ultima: Pick<VersionGuardada, 'creadoEn' | 'huella'> | null,
  huellaActual: string,
  ahora: string,
  minutos = MINUTOS_ENTRE_AUTOMATICAS,
): boolean {
  if (!ultima) return true;
  if (ultima.huella === huellaActual) return false;
  const transcurrido = Date.parse(ahora) - Date.parse(ultima.creadoEn);
  return !(transcurrido < minutos * 60_000);
}

/** Bytes de un texto en UTF-8 (lo que ocupa guardado o exportado). */
export function bytesDeTexto(texto: string): number {
  return new TextEncoder().encode(texto).length;
}

export interface Instantanea {
  datos: Uint8Array | string;
  comprimida: boolean;
}

/** Comprime el texto con gzip si el navegador sabe hacerlo; si no, lo guarda tal cual. */
export async function comprimir(texto: string): Promise<Instantanea> {
  if (typeof CompressionStream === 'undefined') return { datos: texto, comprimida: false };
  const flujo = new Blob([texto]).stream().pipeThrough(new CompressionStream('gzip'));
  return { datos: new Uint8Array(await new Response(flujo).arrayBuffer()), comprimida: true };
}

export async function descomprimir(i: Instantanea): Promise<string> {
  if (!i.comprimida)
    return typeof i.datos === 'string' ? i.datos : new TextDecoder().decode(i.datos);
  const bytes = typeof i.datos === 'string' ? new TextEncoder().encode(i.datos) : i.datos;
  const flujo = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(flujo).text();
}

export const tamanoDeInstantanea = (i: Instantanea): number =>
  typeof i.datos === 'string' ? bytesDeTexto(i.datos) : i.datos.byteLength;
